// /app/frontend/src/utils/offlineManager.js
// Offline support manager for measurement projects
// Uses IndexedDB for persistent storage and handles sync queue

const DB_NAME = 'apollo-offline-db';
const DB_VERSION = 1;
const STORES = {
  PROJECTS: 'measurementProjects',
  SYNC_QUEUE: 'syncQueue',
  OPTIONS: 'options',
  PENDING_PHOTOS: 'pendingPhotos',
};

class OfflineManager {
  constructor() {
    this.db = null;
    this.isOnline = navigator.onLine;
    this.listeners = new Set();
    
    // Listen for online/offline events
    window.addEventListener('online', () => this.handleOnlineStatusChange(true));
    window.addEventListener('offline', () => this.handleOnlineStatusChange(false));
  }

  // Initialize IndexedDB
  async init() {
    if (this.db) return this.db;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Store for offline projects (keyed by ID)
        if (!db.objectStoreNames.contains(STORES.PROJECTS)) {
          const projectStore = db.createObjectStore(STORES.PROJECTS, { keyPath: 'id' });
          projectStore.createIndex('status', 'status', { unique: false });
          projectStore.createIndex('syncStatus', 'syncStatus', { unique: false });
        }

        // Store for sync queue (operations to sync when online)
        if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
          const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id', autoIncrement: true });
          syncStore.createIndex('timestamp', 'timestamp', { unique: false });
          syncStore.createIndex('type', 'type', { unique: false });
        }

        // Store for cached options (dropdowns, etc.)
        if (!db.objectStoreNames.contains(STORES.OPTIONS)) {
          db.createObjectStore(STORES.OPTIONS, { keyPath: 'key' });
        }

        // Store for pending photo uploads
        if (!db.objectStoreNames.contains(STORES.PENDING_PHOTOS)) {
          const photoStore = db.createObjectStore(STORES.PENDING_PHOTOS, { keyPath: 'id' });
          photoStore.createIndex('projectId', 'projectId', { unique: false });
          photoStore.createIndex('windowId', 'windowId', { unique: false });
        }
      };
    });
  }

  // Handle online/offline status changes
  handleOnlineStatusChange(isOnline) {
    this.isOnline = isOnline;
    this.notifyListeners({ type: 'status', isOnline });
    
    if (isOnline) {
      // Trigger sync when coming back online
      this.notifyListeners({ type: 'sync-available' });
    }
  }

  // Subscribe to offline manager events
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  notifyListeners(event) {
    this.listeners.forEach(cb => cb(event));
  }

  // ============== PROJECT OPERATIONS ==============

  // Save project locally (for offline use or as cache)
  async saveProjectLocally(project, syncStatus = 'synced') {
    await this.init();
    
    const projectData = {
      ...project,
      syncStatus, // 'synced', 'pending', 'modified', 'new'
      lastModified: new Date().toISOString(),
    };

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.PROJECTS, 'readwrite');
      const store = tx.objectStore(STORES.PROJECTS);
      const request = store.put(projectData);
      
      request.onsuccess = () => resolve(projectData);
      request.onerror = () => reject(request.error);
    });
  }

  // Get all locally stored projects
  async getLocalProjects() {
    await this.init();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.PROJECTS, 'readonly');
      const store = tx.objectStore(STORES.PROJECTS);
      const request = store.getAll();
      
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  // Get a single project by ID
  async getLocalProject(id) {
    await this.init();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.PROJECTS, 'readonly');
      const store = tx.objectStore(STORES.PROJECTS);
      const request = store.get(id);
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Delete a local project
  async deleteLocalProject(id) {
    await this.init();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.PROJECTS, 'readwrite');
      const store = tx.objectStore(STORES.PROJECTS);
      const request = store.delete(id);
      
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Get projects pending sync
  async getPendingProjects() {
    await this.init();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.PROJECTS, 'readonly');
      const store = tx.objectStore(STORES.PROJECTS);
      const index = store.index('syncStatus');
      const results = [];
      
      // Get 'new', 'modified', and 'pending' projects
      const request = store.openCursor();
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          if (['new', 'modified', 'pending'].includes(cursor.value.syncStatus)) {
            results.push(cursor.value);
          }
          cursor.continue();
        } else {
          resolve(results);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  // ============== SYNC QUEUE OPERATIONS ==============

  // Add operation to sync queue
  async addToSyncQueue(operation) {
    await this.init();

    const queueItem = {
      ...operation,
      timestamp: new Date().toISOString(),
      retries: 0,
    };

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.SYNC_QUEUE, 'readwrite');
      const store = tx.objectStore(STORES.SYNC_QUEUE);
      const request = store.add(queueItem);
      
      request.onsuccess = () => {
        this.notifyListeners({ type: 'queue-updated' });
        resolve(request.result);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Get all items in sync queue
  async getSyncQueue() {
    await this.init();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.SYNC_QUEUE, 'readonly');
      const store = tx.objectStore(STORES.SYNC_QUEUE);
      const index = store.index('timestamp');
      const request = index.getAll();
      
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  // Remove item from sync queue
  async removeFromSyncQueue(id) {
    await this.init();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.SYNC_QUEUE, 'readwrite');
      const store = tx.objectStore(STORES.SYNC_QUEUE);
      const request = store.delete(id);
      
      request.onsuccess = () => {
        this.notifyListeners({ type: 'queue-updated' });
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Clear entire sync queue
  async clearSyncQueue() {
    await this.init();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.SYNC_QUEUE, 'readwrite');
      const store = tx.objectStore(STORES.SYNC_QUEUE);
      const request = store.clear();
      
      request.onsuccess = () => {
        this.notifyListeners({ type: 'queue-updated' });
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Get sync queue count
  async getSyncQueueCount() {
    await this.init();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.SYNC_QUEUE, 'readonly');
      const store = tx.objectStore(STORES.SYNC_QUEUE);
      const request = store.count();
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // ============== OPTIONS CACHE ==============

  // Cache options (for offline dropdowns)
  async cacheOptions(key, data) {
    await this.init();

    const cacheItem = {
      key,
      data,
      cachedAt: new Date().toISOString(),
    };

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.OPTIONS, 'readwrite');
      const store = tx.objectStore(STORES.OPTIONS);
      const request = store.put(cacheItem);
      
      request.onsuccess = () => resolve(cacheItem);
      request.onerror = () => reject(request.error);
    });
  }

  // Get cached options
  async getCachedOptions(key) {
    await this.init();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.OPTIONS, 'readonly');
      const store = tx.objectStore(STORES.OPTIONS);
      const request = store.get(key);
      
      request.onsuccess = () => resolve(request.result?.data);
      request.onerror = () => reject(request.error);
    });
  }

  // ============== PENDING PHOTOS ==============

  // Save photo for later upload
  async savePendingPhoto(photoData) {
    await this.init();

    const photo = {
      id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      ...photoData,
      savedAt: new Date().toISOString(),
    };

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.PENDING_PHOTOS, 'readwrite');
      const store = tx.objectStore(STORES.PENDING_PHOTOS);
      const request = store.add(photo);
      
      request.onsuccess = () => resolve(photo);
      request.onerror = () => reject(request.error);
    });
  }

  // Get pending photos for a project
  async getPendingPhotos(projectId) {
    await this.init();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.PENDING_PHOTOS, 'readonly');
      const store = tx.objectStore(STORES.PENDING_PHOTOS);
      const index = store.index('projectId');
      const request = index.getAll(projectId);
      
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  // Remove pending photo
  async removePendingPhoto(id) {
    await this.init();

    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.PENDING_PHOTOS, 'readwrite');
      const store = tx.objectStore(STORES.PENDING_PHOTOS);
      const request = store.delete(id);
      
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // ============== UTILITY METHODS ==============

  // Check if online
  getOnlineStatus() {
    return navigator.onLine;
  }

  // Generate temporary ID for offline-created items
  generateTempId() {
    return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Convert file to base64 for local storage
  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // Clear all local data
  async clearAllData() {
    await this.init();

    const stores = [STORES.PROJECTS, STORES.SYNC_QUEUE, STORES.OPTIONS, STORES.PENDING_PHOTOS];
    
    for (const storeName of stores) {
      await new Promise((resolve, reject) => {
        const tx = this.db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        const request = store.clear();
        request.onsuccess = resolve;
        request.onerror = reject;
      });
    }
    
    this.notifyListeners({ type: 'data-cleared' });
  }
}

// Export singleton instance
export const offlineManager = new OfflineManager();
export default offlineManager;
